import classnames from 'classnames';
import {
  IncludeTemplateIcon as includeTemplateIcon,
  QuestMarkIcon as questMarkIcon,
  OverviewCircularProgress,
} from '@/shared/ui';
import useTemplatePreview from './useTemplatePreview';
import './styles.scss';

export type TemplatePreviewProps = {
  id?: string;
  onClickSelect: (template: any) => void;
  gain?: number;
  disabled?: boolean;
  className?: string;
  highPass?: number;
  isIncluded?: boolean;
  isActive?: boolean;
  isSmallSize?: boolean;
  template: any;
  isUnclassified?: boolean;
  beatCount?: number;
  ecgDataMap: any;
  name?: string;
  hideStatus?: boolean;
  selectedTemplate: any;
  reviewedBeatsChanges: any[];
  bulkReviewedBeats: any[];
  resetBulkReviewedBeats: any[];
};

/**
 * Renders a single beat morphology template card with its name, beat count and reviewed progress. Draws the template waveform into an SVG sized by useTemplatePreview, substitutes a question-mark icon for unclassified templates, and shows the include-in-report icon and the circular progress ring only when hideStatus is off and the reviewed percentage is above zero.
 *
 * @param props - Template card inputs including the template, its id, name, beat count, gain, size and the active, included and hidden-status flags.
 * @returns The template preview button with its waveform, labels and status icons.
 */
const TemplatePreview = ({
  id,
  onClickSelect,
  gain,
  disabled,
  className,
  highPass,
  isIncluded,
  isActive,
  isSmallSize,
  template,
  isUnclassified,
  beatCount,
  ecgDataMap,
  name,
  hideStatus,
  selectedTemplate,
  reviewedBeatsChanges,
  bulkReviewedBeats,
  resetBulkReviewedBeats,
}: TemplatePreviewProps) => {
  const { size, canvasRef, onClick, percentReviewed } = useTemplatePreview({
    onClickSelect,
    gain,
    highPass,
    isSmallSize,
    template,
    isUnclassified,
    ecgDataMap,
    hideStatus,
    selectedTemplate,
    reviewedBeatsChanges,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
  });
  return (
    <div id={id} className="template-preview-container">
      <button
        id={id}
        disabled={disabled}
        className={classnames(
          'template-preview-wrapper',
          className,
          isActive ? '--active' : '',
          isSmallSize ? '--small' : '',
        )}
        onClick={onClick}
      >
        {isUnclassified ? (
          <div className="template-preview-box">
            <img src={questMarkIcon} alt="" />
          </div>
        ) : (
          <div
            className={classnames(
              'template-preview-box --template',
              isSmallSize ? '--small-box' : '',
            )}
          >
            <svg ref={canvasRef} width={size * 0.5} height={size} />
          </div>
        )}
        <div className="template-left-top-conner">{name}</div>
        {!isUnclassified && !hideStatus && (
          <div className="template-right-top-conner">
            {isIncluded && <img src={includeTemplateIcon} alt="" />}
            {/* percentReviewed can be a string (toFixed result) compared/passed as a number; pre-existing. */}
            {(percentReviewed as any) > 0 && (
              <OverviewCircularProgress
                // isActive={activeButton === eventType}
                isActive
                name={`beatEvent-${id}`}
                index={id as any}
                value={percentReviewed as any}
                minDisplayText={0.1}
                minDisplay={6}
              />
            )}
          </div>
        )}
        <div className="bottom-content">{`Beats: ${beatCount}`}</div>
      </button>
    </div>
  );
};

export default TemplatePreview;
